import { ChevronDownIcon } from '@chakra-ui/icons';
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Center,
  Flex,
  HStack,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useColorModeValue,
} from '@chakra-ui/react';

import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';

import { LogEntry } from 'api/logRepository';
import { SearchBar } from 'components/navbar/searchBar/SearchBar';
import React, { Dispatch, SetStateAction, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

type Props = {
  tableData: LogEntry[];
  globalFilter: string;
  setGlobalFilter: Dispatch<SetStateAction<string>>
};

export default function LoginEntryTable({ tableData , globalFilter, setGlobalFilter}: Props) {
  const textColor = useColorModeValue('gray.700', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
  const subTextColor = useColorModeValue('gray.500', 'gray.400');
  const hoverBg = useColorModeValue('blue.50', 'whiteAlpha.100');
  const cellColor = useColorModeValue('secondaryGray.900', 'white');
  const navigate = useNavigate();

  const [data, setData] = useState<LogEntry[]>(tableData);
  const [levelFilter, setLevelFilter] = useState<string>('');
  const [sourceFilter, setSourceFilter] = useState<string>('');
  const [subSourceFilter, setSubSourceFilter] = useState<string>('');

  const [selectedLog, setSelectedLog] = useState<LogEntry | null>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);

  // Center alert state
  const [centerAlertVisible, setCenterAlertVisible] = useState(false);
  const [centerAlertStatus, setCenterAlertStatus] = useState<
    "success" | "error" | "info" | "warning"
  >("info");
  const [centerAlertMessage, setCenterAlertMessage] = useState("");

  const showCenteredAlert = (
    status: "success" | "error" | "info" | "warning",
    message: string
  ) => {
    setCenterAlertStatus(status);
    setCenterAlertMessage(message);
    setCenterAlertVisible(true);
    setTimeout(() => setCenterAlertVisible(false), 2000);
  };

  const headerTextStyle = {
    fontSize: '12px',
    fontWeight: '700',
    letterSpacing: '0.6px',
    textTransform: 'uppercase' as const,
    color: 'gray.400',
  };

  const cellTextStyle = {
    fontSize: '15px',
    fontWeight: '500',
    color: cellColor,
  };

  // Table Columns
  const columnHelper = createColumnHelper<LogEntry>();

  const columns = useMemo(
    () => [
      columnHelper.accessor('timestamp', {
        header: () => <Text sx={headerTextStyle}>Timestamp</Text>,
        cell: (info) => (
          <Text sx={cellTextStyle}>
            {new Date(info.getValue()).toLocaleString()}
          </Text>
        ),
      }),

      columnHelper.accessor('level', {
        header: () => <Text sx={headerTextStyle}>Level</Text>,
        cell: (info) => {
          const val = info.getValue();
          const map: any = {
            Info: "blue",
            Warning: "yellow",
            Error: "red",
            Critical: "red",
          };
          return (
            <Badge colorScheme={map[val]} fontSize="15px">{val}</Badge>
          );
        },
      }),

      columnHelper.accessor('message', {
        header: () => <Text sx={headerTextStyle}>Message</Text>,
        cell: (info) => (
          <Text color={subTextColor} fontSize="sm" fontWeight='500'>
            {info.getValue()}
          </Text>
        ),
      }),

      columnHelper.accessor('source', {
        header: () => <Text sx={headerTextStyle}>Source</Text>,
        cell: (info) => (
          <Text sx={cellTextStyle}>
            {info.getValue()}
          </Text>
        ),
      }),

      columnHelper.accessor('subSource', {
        header: () => <Text sx={headerTextStyle}>Sub Source</Text>,
        cell: (info) => (
          <Text sx={cellTextStyle}>
            {info.getValue() || "-"}
          </Text>
        ),
      }),

      columnHelper.accessor('context', {
        header: () => <Text sx={headerTextStyle}>Context</Text>,
        cell: (info) => {
          const ctx = info.getValue();
          if (!ctx) return <Text color={subTextColor}>-</Text>;

          const fullText = Object.entries(ctx)
            .map(([k, v]) => `${k}: ${v}`)
            .join(", ");

          const shortText =
            fullText.length > 30 ? fullText.substring(0, 30) + "..." : fullText;

          return (
            <Tooltip label={fullText} placement="top" hasArrow>
              <Text color={subTextColor} fontSize="sm" fontWeight='500' noOfLines={1}>
                {shortText}
              </Text>
            </Tooltip>
          );
        },
      }),

      columnHelper.accessor('exception', {
        header: () => <Text sx={headerTextStyle}>Exception</Text>,
        cell: (info) => {
          const value = info.getValue();
          if (!value) return <Text color={subTextColor}>-</Text>;

          const shortText = value.length > 30 ? value.substring(0, 30) + "..." : value;

          return (
            <Tooltip label={value} placement="top" hasArrow>
              <Text color={subTextColor} fontSize="sm" noOfLines={1}>
                {shortText}
              </Text>
            </Tooltip>
          );
        },
      }),

      columnHelper.accessor('isActive', {
        header: () => <Text sx={headerTextStyle}>Status</Text>,
        cell: (info) =>
          info.getValue() ? (
            <Badge fontSize='15px' colorScheme="green">Active</Badge>
          ) : (
            <Badge fontSize='15px' colorScheme="red">Inactive</Badge>
          ),
      }),
    ],
    [navigate, textColor, subTextColor]
  );

  const table = useReactTable({
    data,
    columns,
    state: { globalFilter },
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  // Filtering by Level
  const handleLevelFilter = async (level: string) => {
    setLevelFilter(level);
    if (!level) {
      setData(tableData);
      return;
    }
    const filtered = tableData.filter(l => l.level === level);
    setData(filtered);
  };

  const uniqueSources = useMemo(
    () => Array.from(new Set(tableData.map(l => l.source).filter(Boolean))),
    [tableData]
  );

  const uniqueSubSources = useMemo(
    () => Array.from(new Set(tableData.map(l => l.subSource).filter(Boolean))),
    [tableData]
  );

  const filteredData = useMemo(() => {
    let data = [...tableData];

    if (levelFilter) {
      data = data.filter(l => l.level === levelFilter);
    }

    if (sourceFilter) {
      data = data.filter(l => l.source === sourceFilter);
    }

    if (subSourceFilter) {
      data = data.filter(l => l.subSource === subSourceFilter);
    }

    return data;
  }, [tableData, levelFilter, sourceFilter, subSourceFilter]);

  React.useEffect(() => {
    setData(filteredData);
  }, [filteredData]);


  return (
    <Flex direction="column" w="100%" minH="calc(100vh - 75px)" overflowX="auto">

      <Flex justify='space-between' align='center' mb={4}>
        <HStack spacing={3}>
          <DebouncedInput
            value={globalFilter ?? ''}
            onChange={(value) => setGlobalFilter(String(value))}
            placeholder="Search logs..."
          />
          {/* Source Filter */}
          <Menu>
            <MenuButton
              as={Button}
              rightIcon={<ChevronDownIcon />}
              bg={useColorModeValue('gray.100', 'gray.700')}
              borderRadius="full"
            >
              {sourceFilter || 'Source'}
            </MenuButton>

            <MenuList>
              <MenuItem onClick={() => setSourceFilter('')}>All</MenuItem>
              {uniqueSources.map(src => (
                <MenuItem key={src} onClick={() => setSourceFilter(src)}>
                  {src}
                </MenuItem>
              ))}
            </MenuList>
          </Menu>
          {/* Sub Source Filter */}
          <Menu>
            <MenuButton
              as={Button}
              rightIcon={<ChevronDownIcon />}
              bg={useColorModeValue('gray.100', 'gray.700')}
              borderRadius="full"
            >
              {subSourceFilter || 'Sub Source'}
            </MenuButton>

            <MenuList>
              <MenuItem onClick={() => setSubSourceFilter('')}>All</MenuItem>
              {uniqueSubSources.map(sub => (
                <MenuItem key={sub} onClick={() => setSubSourceFilter(sub)}>
                  {sub}
                </MenuItem>
              ))}
            </MenuList>
          </Menu>
          {/* Level Filter */}
          <Menu>
            <MenuButton
              as={Button}
              rightIcon={<ChevronDownIcon />}
              bg={useColorModeValue('gray.100', 'gray.700')}
              borderRadius="full"
              _hover={{ bg: useColorModeValue('gray.200', 'gray.600') }}
            >
              {levelFilter || 'Level'}
            </MenuButton>
            <MenuList>
              <MenuItem onClick={() => handleLevelFilter('')}>All</MenuItem>
              <MenuItem onClick={() => handleLevelFilter('Info')}>Info</MenuItem>
              <MenuItem onClick={() => handleLevelFilter('Warning')}>Warning</MenuItem>
              <MenuItem onClick={() => handleLevelFilter('Error')}>Error</MenuItem>
              <MenuItem onClick={() => handleLevelFilter('Critical')}>Critical</MenuItem>
            </MenuList>
          </Menu>
        </HStack>
      </Flex>

      <Box overflowX="auto">
        <Table variant="simple" color="gray.500">
          <Thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <Tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <Th
                    key={header.id}
                    onClick={header.column.getToggleSortingHandler()}
                    cursor="pointer"
                    textTransform="uppercase"
                    borderColor={borderColor}
                    align="center"
                  >
                    {flexRender(header.column.columnDef.header, header.getContext())}
                  </Th>
                ))}
              </Tr>
            ))}
          </Thead>

          <Tbody>
            {table.getRowModel().rows.length > 0 ? (
              table.getRowModel().rows.map((row) => (
                <Tr
                  key={row.id}
                  sx={{
                    '& td': {
                      borderBottom: '1px solid',
                      borderColor: borderColor,
                      cursor: 'pointer',
                    },
                    '&:hover td': { backgroundColor: hoverBg },
                  }}
                >
                  {row.getVisibleCells().map((cell) => (
                    <Td key={cell.id} borderColor={borderColor} minW="150px">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </Td>
                  ))}
                </Tr>
              ))
            ) : (
              <Tr>
                <Td colSpan={columns.length} textAlign="center">
                  No log entries found.
                </Td>
              </Tr>
            )}
          </Tbody>
        </Table>
      </Box>


      {/* Center Alert */}
      {centerAlertVisible && (
        <Center
          position="fixed"
          top="0"
          left="0"
          right="0"
          bottom="0"
          zIndex={60}
          pointerEvents="none"
        >
          <Alert
            status={centerAlertStatus}
            variant="subtle"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            textAlign="center"
            width="600px"
            borderRadius="14px"
            boxShadow="lg"
            py={10}
            px={16}
            pointerEvents="auto"
          >
            <AlertIcon boxSize="40px" mr={0} />
            <Text fontSize="lg" fontWeight="700" mt={1}>
              {centerAlertMessage}
            </Text>
          </Alert>
        </Center>
      )}
    </Flex>
  );
}

/* DebouncedInput */
function DebouncedInput({
  value: initialValue,
  onChange,
  debounce = 500,
  ...props
}: {
  value: string | number;
  onChange: (value: string | number) => void;
  debounce?: number;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'>) {
  const [value, setValue] = useState(initialValue);

  React.useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  React.useEffect(() => {
    const timeout = setTimeout(() => {
      onChange(value);
    }, debounce);
    return () => clearTimeout(timeout);
  }, [value, debounce, onChange]);

  return (
    <SearchBar
      {...props}
      value={value}
      onChange={(e: any) => setValue(e.target.value)}
      h="44px"
      w={{ lg: '390px' }}
      borderRadius="16px"
    />
  );
}